/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';
import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {StateDeclarationBuilder, AdnRouterHelperApi} from '../routing/state-utils';
import {CampaignContextActionsComponent} from './campaign-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {emptyRouteView} from '../common/directives/html/route-view-components';
import {LineItemApiService, LineItemModel} from '../line-item/line-item-api.service';
import {CampaignEditPageComponent} from './campaign-edit-page.component';
import {CampaignListPageComponent} from './campaign-list-page.component';

export function campaignStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
const lineItemApi = adnRouterHelper.services.service(LineItemApiService);
const permissions = adnRouterHelper.services.localUserPermissions();
const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
const readOnly = () => !permissions.hasAny(PERMISSIONS.manageAdvertising.apiKey)
  && !permissions.hasAny(PERMISSIONS.axAdvertiser.apiKey);
let rh = adnRouterHelper.createRouterHelper('campaigns.campaign', LineItemApiService, 'app.adv', 'app');
    
        states.add(rh.listPath(), rh.list({
          resolve: {
            listConfig: function() {
              return {listMethod: 'list', moreParams: {excludeIfMissing: 'productId'}, listIdOverride: 'Campaign'};
            }
          },
          views: {
            'content@app': {component: CampaignListPageComponent, bindings: {readOnly: 'listReadOnly'}},
            'context@app': emptyRouteView()
          }
        }))
          .add(rh.newItemPath(), rh.newItem())
          .add(rh.itemPath(), rh.item({
            resolve: {
              lineItemView: function($stateParams) {
                const dataViewApi = adnRouterHelper.services.dataViewApi();

                if (!$stateParams.isNew) {
                  return firstValueFrom(dataViewApi.getView(
                    'LineItemView', {id: $stateParams.id || 'undefined-id'}, false
                  ));
                }
              },
              campaignPage: function($stateParams) {
                const createState = (model: LineItemModel, isNew: boolean) => new SignalAdminResourcePageState(
                  model, isNew, readOnly(), lineItemApi, 'Campaign', 'name', undefined, undefined,
                  {objectTypeKey: 'LineItem', crumbParams: ['Advertiser', 'Order']},
                  {warningMessage: 'The {{action}} of the Campaign will also stop it from serving ads.', object: 'Campaign'}
                );
                if ($stateParams.isNew) {
                  return createState({
                    id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: [], userState: 'RESERVED'
                  }, true);
                }
                return firstValueFrom(lineItemApi.get($stateParams.id).pipe(
                  tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
                )).then((model) => createState(model, false));
              },
              model: function(campaignPage: SignalAdminResourcePageState<LineItemModel>) {
                return campaignPage.model();
              }
            },
            views: {
              'content@app': {
                component: CampaignEditPageComponent,
                bindings: {state: 'campaignPage', lineItemView: 'lineItemView'}
              },
              'context@app': {
                component: CampaignContextActionsComponent,
                bindings: {state: 'campaignPage'}
              },
              'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'campaignPage'}}
            }
          }));
  return states.build();
}
